// src/screens/AppointmentScreen.js
import React, { useContext, useEffect, useState, useMemo } from 'react';
import {
  useParams,
  useNavigate,
  Link,
} from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { vehiclesAPI, dealersAPI, appointmentsAPI } from '../services/api';
import AppHeader from '../components/AppHeader';
import WaiverAcceptance from '../components/WaiverAcceptance';
import { MapPin, Phone, Mail, Globe, CheckCircle, Star, Clock, Calendar, Car } from 'lucide-react';

const AppointmentScreen = () => {
  const { id } = useParams(); // vehicle id from /vehicle/:id/appointment
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Accepted waiver version (null until the box is ticked); required per booking.
  const [waiverVersion, setWaiverVersion] = useState(null);
  // Bumped to reload the waiver when the server says it has changed.
  const [waiverKey, setWaiverKey] = useState(0);

  // vehicle + dealer info
  const [vehicle, setVehicle] = useState(null);
  const [loadingVehicle, setLoadingVehicle] = useState(true);

  // available slots for this vehicle at its dealer
  const [slots, setSlots] = useState([]);
  const [appointmentDuration, setAppointmentDuration] = useState(null);
  const [hasCalendarSetup, setHasCalendarSetup] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState('');

  // date filtering
  const [selectedDate, setSelectedDate] = useState('');

  // submission state
  const [submitting, setSubmitting] = useState(false);

  // Require login
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Load vehicle
  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        setLoadingVehicle(true);
        const { data } = await vehiclesAPI.getOne(id);
        setVehicle(data);
      } catch (err) {
        console.error('Error fetching vehicle', err);
      } finally {
        setLoadingVehicle(false);
      }
    };

    if (id) {
      fetchVehicle();
    }
  }, [id]);

  // Load available slots when vehicle (and its dealer) is known
  useEffect(() => {
    const fetchSlots = async () => {
      if (!vehicle?.dealer?._id) {
        setSlots([]);
        return;
      }
      try {
        setLoadingSlots(true);
        const dealerId = vehicle.dealer._id;
        const { data } = await dealersAPI.getVehicleAvailableSlots(dealerId, id);
        setSlots(data.slots || []);
        setAppointmentDuration(data.appointmentDuration || null);
        setHasCalendarSetup(data.hasCalendarSetup !== false);
      } catch (err) {
        console.error('Error fetching available slots', err);
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchSlots();
  }, [vehicle, id]);

  // Get unique dates from slots for date filter
  const availableDates = useMemo(() => {
    const dateSet = new Set();
    slots.forEach((s) => {
      const d = new Date(s.date).toISOString().split('T')[0];
      dateSet.add(d);
    });
    return Array.from(dateSet).sort();
  }, [slots]);

  // Filter slots by selected date
  const filteredSlots = useMemo(() => {
    if (!selectedDate) return slots;
    return slots.filter((s) => {
      const d = new Date(s.date).toISOString().split('T')[0];
      return d === selectedDate;
    });
  }, [slots, selectedDate]);

  // Group filtered slots by date for display
  const slotsByDate = useMemo(() => {
    const groups = {};
    filteredSlots.forEach((s) => {
      const dateKey = new Date(s.date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(s);
    });
    return groups;
  }, [filteredSlots]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      navigate('/login');
      return;
    }

    if (!vehicle?.dealer?._id) {
      alert('This vehicle is not associated with a dealership.');
      return;
    }

    if (!selectedSlotId) {
      alert('Please select an available time slot.');
      return;
    }

    if (!waiverVersion) {
      alert('Please read and accept the test drive waiver.');
      return;
    }

    try {
      setSubmitting(true);
      await appointmentsAPI.create({
        vehicles: [id],
        dealerId: vehicle.dealer._id,
        slotId: selectedSlotId,
        waiver: { accepted: true, version: waiverVersion },
      });

      navigate('/appointments');
    } catch (err) {
      console.error('Error creating appointment', err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to create appointment.';
      alert(msg);
      if (err.response?.data?.code === 'WAIVER_OUTDATED') {
        setWaiverKey((k) => k + 1); // show the new waiver; it must be accepted again
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  const dealer = vehicle?.dealer;

  // Dealer open/closed status
  const now = new Date();
  const dayOfWeek = now.getDay();
  const currentTime = now.toTimeString().slice(0, 5);
  const todayHours = dealer?.hours?.find((h) => h.day === dayOfWeek);
  const isOpen =
    todayHours &&
    todayHours.isOpen &&
    currentTime >= todayHours.open &&
    currentTime <= todayHours.close;

  return (
    <div className="flex flex-col h-full customer-friendly">
      <AppHeader title="Book Your Test Drive" subtitle="Pick a time that works for you!" />

      <div className="flex-1 overflow-y-auto p-4 pb-20 space-y-4 text-sm">
        {/* Vehicle summary */}
        {loadingVehicle ? (
          <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Loading vehicle...</div>
        ) : vehicle ? (
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-3 border border-app-border-light dark:border-app-border flex gap-3">
            <img
              src={vehicle.image}
              alt={`${vehicle.make} ${vehicle.model}`}
              className="w-20 h-16 rounded-xl object-cover"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Car className="w-4 h-4 text-customer-teal" />
                <span className="font-semibold text-sm">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </span>
              </div>
              <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">
                {vehicle.mileage
                  ? `${vehicle.mileage.toLocaleString()} mi`
                  : 'Mileage N/A'}{' '}
                &bull; {vehicle.type}
              </div>
              <div className="text-[11px] text-app-accent mt-1">
                {vehicle.price
                  ? `$${vehicle.price.toLocaleString()}`
                  : 'Price N/A'}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
            Vehicle not found.{' '}
            <Link to="/" className="text-app-accent">
              Back to Home
            </Link>
          </div>
        )}

        {/* Dealer info (auto-selected from vehicle) */}
        {dealer && (
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-3 border border-app-border-light dark:border-app-border space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-app-text-secondary-light dark:text-app-text-secondary font-medium">
              Dealership
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/dealer/${dealer._id}/info`}
                className="font-semibold text-sm text-customer-teal hover:text-customer-teal-hover transition-colors"
              >
                {dealer.name}
              </Link>
              {dealer.verified && (
                <CheckCircle className="w-4 h-4 text-app-success" title="Verified Dealer" />
              )}
              {dealer.rating && dealer.rating.count > 0 && (
                <div className="flex items-center gap-1 ml-auto">
                  <Star className="w-3 h-3 fill-app-warning text-app-warning" />
                  <span className="text-[10px] font-semibold text-app-text-light dark:text-app-text">
                    {dealer.rating.average.toFixed(1)}
                  </span>
                </div>
              )}
            </div>

            {dealer.address && (
              <div className="flex items-start gap-2 text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">
                <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>
                  {[
                    dealer.address.street,
                    dealer.address.city,
                    dealer.address.state && dealer.address.zip
                      ? `${dealer.address.state} ${dealer.address.zip}`
                      : dealer.address.state || dealer.address.zip,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              </div>
            )}

            {dealer.phone && (
              <div className="flex items-center gap-2 text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">
                <Phone className="w-3 h-3" />
                <a href={`tel:${dealer.phone}`} className="hover:text-customer-teal transition-colors">
                  {dealer.phone}
                </a>
              </div>
            )}

            {dealer.email && (
              <div className="flex items-center gap-2 text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">
                <Mail className="w-3 h-3" />
                <a href={`mailto:${dealer.email}`} className="hover:text-customer-teal transition-colors">
                  {dealer.email}
                </a>
              </div>
            )}

            {dealer.website && (
              <div className="flex items-center gap-2 text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">
                <Globe className="w-3 h-3" />
                <a
                  href={dealer.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-customer-teal transition-colors"
                >
                  {dealer.website.replace(/^https?:\/\//, '')}
                </a>
              </div>
            )}

            {dealer.hours && dealer.hours.length > 0 && (
              <div className="flex items-center gap-2 text-[11px]">
                <Clock className={`w-3 h-3 ${isOpen ? 'text-app-success' : 'text-app-error'}`} />
                <span className={`font-medium ${isOpen ? 'text-app-success' : 'text-app-error'}`}>
                  {isOpen ? 'Open Now' : 'Closed'}
                </span>
                {todayHours && todayHours.isOpen && (
                  <span className="text-app-text-secondary-light dark:text-app-text-secondary">
                    ({todayHours.open} - {todayHours.close})
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* No dealer warning */}
        {!loadingVehicle && vehicle && !dealer && (
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border text-center">
            <div className="text-app-text-secondary-light dark:text-app-text-secondary text-xs">
              This vehicle is not currently associated with a dealership and cannot be booked for a test drive.
            </div>
            <Link to="/" className="text-app-accent text-xs mt-2 inline-block">
              Browse other vehicles
            </Link>
          </div>
        )}

        {/* Time slots section */}
        {dealer && (
          <form onSubmit={handleSubmit} className="space-y-3 text-xs">
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-3 border border-app-border-light dark:border-app-border space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-customer-teal" />
                  Available Time Slots
                </div>
                {appointmentDuration && (
                  <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary">
                    {appointmentDuration} min per session
                  </div>
                )}
              </div>

              {/* Date filter */}
              {availableDates.length > 0 && (
                <div>
                  <label className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary uppercase tracking-wider">
                    Filter by date
                  </label>
                  <div className="flex gap-2 mt-1 overflow-x-auto pb-1">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDate('');
                        setSelectedSlotId('');
                      }}
                      className={`px-3 py-1.5 rounded-xl text-[11px] whitespace-nowrap border transition-all ${
                        selectedDate === ''
                          ? 'border-customer-teal bg-customer-teal/10 text-customer-teal font-medium'
                          : 'border-app-input-border-light dark:border-app-input-border text-app-text-secondary-light dark:text-app-text-secondary hover:border-customer-teal/50'
                      }`}
                    >
                      All dates
                    </button>
                    {availableDates.map((d) => (
                      <button
                        type="button"
                        key={d}
                        onClick={() => {
                          setSelectedDate(d);
                          setSelectedSlotId('');
                        }}
                        className={`px-3 py-1.5 rounded-xl text-[11px] whitespace-nowrap border transition-all ${
                          selectedDate === d
                            ? 'border-customer-teal bg-customer-teal/10 text-customer-teal font-medium'
                            : 'border-app-input-border-light dark:border-app-input-border text-app-text-secondary-light dark:text-app-text-secondary hover:border-customer-teal/50'
                        }`}
                      >
                        {new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Slot grid */}
              {loadingSlots ? (
                <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">
                  Checking availability...
                </div>
              ) : filteredSlots.length === 0 ? (
                <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary py-4 text-center">
                  {slots.length === 0
                    ? hasCalendarSetup
                      ? 'No available time slots for this vehicle right now. Please check back later.'
                      : 'No available time slots for this vehicle right now. The dealership may not have set up their calendar yet.'
                    : 'No slots available on this date. Try another date.'}
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(slotsByDate).map(([dateLabel, dateSlots]) => (
                    <div key={dateLabel}>
                      <div className="text-[10px] uppercase tracking-wider text-app-text-secondary-light dark:text-app-text-secondary font-medium mb-1.5">
                        {dateLabel}
                      </div>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {dateSlots.map((slot) => (
                          <button
                            type="button"
                            key={slot._id}
                            onClick={() => setSelectedSlotId(slot._id)}
                            className={`px-2 py-2.5 rounded-xl border-2 text-xs text-center transition-all duration-200 ${
                              selectedSlotId === slot._id
                                ? 'border-customer-teal bg-customer-teal-light shadow-md shadow-customer-teal/20'
                                : 'border-app-input-border-light dark:border-app-input-border bg-white dark:bg-app-input hover:border-customer-teal/50'
                            }`}
                          >
                            <div
                              className={`font-semibold ${
                                selectedSlotId === slot._id
                                  ? 'text-customer-teal'
                                  : 'text-app-text-light dark:text-app-text'
                              }`}
                            >
                              {slot.time}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <WaiverAcceptance key={waiverKey} onChange={setWaiverVersion} />

            <button
              type="submit"
              disabled={submitting || !selectedSlotId || !waiverVersion}
              className={`mt-4 w-full py-4 rounded-2xl text-base font-bold transition-all duration-300 ${
                submitting || !selectedSlotId || !waiverVersion
                  ? 'bg-app-border-light dark:bg-app-border text-app-text-secondary-light dark:text-app-text-secondary'
                  : 'bg-gradient-to-r from-customer-teal to-customer-teal-hover hover:from-customer-teal-hover hover:to-customer-teal-active text-white shadow-lg shadow-customer-teal/30 hover:scale-[1.02]'
              }`}
            >
              {submitting ? 'Booking your spot...' : 'Book It!'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AppointmentScreen;
